import { useMemo } from "react";
import type { DataSnapshot, PortfolioSnapshot, ScoreTier, Skill } from "../lib/types";
import { scoreClassText, TIER_LABEL, tierClass, deltaSign, deltaClass } from "../lib/scoring";
import { navigateToSkill } from "../router";
import { BrandRail } from "../components/BrandRail";
import { KpiStrip } from "../components/KpiStrip";
import { TrendStrip } from "../components/TrendStrip";
import { FreshnessBar } from "../components/FreshnessBar";
import { formatDate } from "../lib/format";

interface Props { data: DataSnapshot }

const TIERS: ScoreTier[] = ["excellent", "good", "needs-work", "poor"];

export function Dashboard({ data }: Props) {
  const { skills, stats, history, meta, snapshots, liveSnapshot } = data;
  const issueCount = (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0);

  const top = useMemo(
    () => [...skills].sort((a, b) => b.combinedPercentage - a.combinedPercentage).slice(0, 8),
    [skills]
  );
  const bottom = useMemo(
    () => [...skills].sort((a, b) => a.combinedPercentage - b.combinedPercentage).slice(0, 8),
    [skills]
  );
  const movers = useMemo(
    () =>
      [...skills]
        .filter((s) => s.delta != null && s.delta !== 0)
        .sort((a, b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0))
        .slice(0, 6),
    [skills]
  );

  const attention = useMemo(() => {
    return [...skills]
      .filter((s) => s.status === "critical" || s.status === "warning" || s.brokenRefs.length > 0)
      .sort((a, b) => (a.fixPriority ?? 5) - (b.fixPriority ?? 5))
      .slice(0, 8);
  }, [skills]);

  const openSkill = (name: string) => {
    navigateToSkill(name);
  };

  const health = stats.statusCounts ?? { healthy: 0, warning: 0, critical: 0 };
  const total = Math.max(1, health.healthy + health.warning + health.critical);
  const healthBars = [
    { key: "healthy",  count: health.healthy,  cls: "bg-good" as const, color: "text-good" as const },
    { key: "warning",  count: health.warning,  cls: "bg-warn" as const, color: "text-warn" as const },
    { key: "critical", count: health.critical, cls: "bg-bad"  as const, color: "text-bad"  as const },
  ];

  return (
    <div className="dashboard-grid grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: stats.total, issues: issueCount }} />

      <main className="min-w-0">
        <FreshnessBar meta={meta} />

        <Hero
          totalSkills={stats.total}
          avg={stats.average}
          trendDelta={stats.trendDelta}
          withLlm={stats.withLlm}
          above85={stats.above85}
          generatedAt={meta.generated}
        />

        <KpiStrip stats={stats} meta={meta} />

        <section className="dash-row-3 grid border-b border-fg md:[grid-template-columns:1.3fr_1fr_1fr]">
          <HealthGauge bars={healthBars} total={total} />
          <TierDistribution bucketCounts={stats.bucketCounts} total={stats.total} />
          <IssueBreakdown stats={stats} issueCount={issueCount} />
        </section>

        <section className="dash-row-2 grid border-b border-fg md:[grid-template-columns:1fr_1fr]">
          <RankList title="// Top performers" skills={top} onOpen={openSkill} />
          <RankList title="// Bottom of the pile" skills={bottom} onOpen={openSkill} />
        </section>

        <section className="dash-row-2 grid border-b border-fg md:[grid-template-columns:1.4fr_1fr]">
          <AttentionQueue items={attention} onOpen={openSkill} />
          <Movers items={movers} onOpen={openSkill} />
        </section>

        <SnapshotActivity snapshots={snapshots} liveSnapshot={liveSnapshot} />

        <TrendStrip history={history} snapshots={snapshots} liveSnapshot={liveSnapshot} />

        <footer className="px-6 py-4 border-t border-rule font-mono text-[10px] uppercase tracking-[0.12em] text-muted flex flex-wrap items-center justify-between gap-2">
          <span>// skill-grinder · Edition 01</span>
          <span>build · {formatDate(meta.generated)}</span>
          <span>{stats.total} skills · {stats.withLlm} llm-augmented</span>
        </footer>
      </main>
    </div>
  );
}

function Hero({
  totalSkills, avg, trendDelta, withLlm, above85, generatedAt,
}: { totalSkills: number; avg: number; trendDelta: number; withLlm: number; above85: number; generatedAt: string }) {
  const trendCls = trendDelta > 0 ? "text-good" : trendDelta < 0 ? "text-bad" : "text-muted";
  const trendSymbol = trendDelta > 0 ? "▲" : trendDelta < 0 ? "▼" : "■";
  return (
    <header className="px-6 pt-6 pb-4 border-b border-fg">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
          // Edition 01 · {formatDate(generatedAt)}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
          {withLlm} llm-augmented · {above85} ≥85
        </span>
      </div>

      <h1 className="sr-only">Overview</h1>
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <span aria-hidden="true" className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-120">
          {avg}<span className="text-accent">%</span>
        </span>
        <div className="flex flex-col gap-1 pb-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
            Average combined score
          </span>
          <span className={`font-mono text-[14px] tabular-nums uppercase tracking-[0.12em] ${trendCls}`}>
            {trendSymbol} {deltaSign(trendDelta)} vs previous run
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
            Across {totalSkills} skills
          </span>
        </div>
      </div>

      <div className="editorial-rule mt-4" />
    </header>
  );
}

function HealthGauge({
  bars, total,
}: { bars: Array<{ key: string; count: number; cls: string; color: string }>; total: number }) {
  return (
    <div className="p-6 border-r border-rule last:border-r-0">
      <SectionLabel>// Health</SectionLabel>
      <div className="flex h-3 mt-3 border border-fg overflow-hidden" role="img" aria-label="Health distribution">
        {bars.map((b) => (
          <span
            key={b.key}
            className={b.cls}
            style={{ width: `${(b.count / total) * 100}%` }}
            title={`${b.key}: ${b.count}`}
          />
        ))}
      </div>
      <ul className="mt-4 grid grid-cols-3 gap-3">
        {bars.map((b) => (
          <li key={b.key}>
            <div className={`font-display font-extrabold text-t-40 tabular-nums leading-[0.9] tracking-[-0.04em] ${b.color}`}>
              {b.count}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted mt-1">
              {b.key}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierDistribution({ bucketCounts, total }: { bucketCounts: Record<ScoreTier, number> | undefined; total: number }) {
  const max = Math.max(1, ...TIERS.map((t) => bucketCounts?.[t] ?? 0));
  return (
    <div className="p-6 border-r border-rule last:border-r-0">
      <SectionLabel>// Tier distribution</SectionLabel>
      <ul className="mt-3 flex flex-col gap-2">
        {TIERS.map((tier) => {
          const count = bucketCounts?.[tier] ?? 0;
          const pct = (count / Math.max(1, total)) * 100;
          const barCls =
            tierClass(tier) === "good" ? "bg-good" :
            tierClass(tier) === "warn" ? "bg-warn" : "bg-bad";
          const colorCls =
            tierClass(tier) === "good" ? "text-good" :
            tierClass(tier) === "warn" ? "text-warn" : "text-bad";
          return (
            <li key={tier}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                  {TIER_LABEL[tier]}
                </span>
                <span className="font-mono text-[12px] tabular-nums">
                  <span className={colorCls}>{count}</span>
                  <span className="text-muted ml-1">{pct.toFixed(0)}%</span>
                </span>
              </div>
              <div className="h-2 border border-fg overflow-hidden bg-bg">
                <div
                  className={`h-full ${barCls}`}
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function IssueBreakdown({ stats, issueCount }: { stats: DataSnapshot["stats"]; issueCount: number }) {
  return (
    <div className="p-6">
      <SectionLabel>// Coverage</SectionLabel>
      <dl className="mt-3 grid grid-cols-2 gap-y-3 gap-x-4">
        <Pair k="Router" v={stats.withRouter} accent={stats.withRouter >= stats.total * 0.5} />
        <Pair k="References" v={stats.withRefs} />
        <Pair k="Structural avg" v={`${stats.averageStructural}%`} />
        <Pair k="Above 85" v={stats.above85} />
        <Pair k="Below 70" v={stats.below70} accent={stats.below70 === 0} />
        <Pair k="Broken refs" v={stats.broken} accent={stats.broken === 0} />
        <Pair k="Open issues" v={issueCount} accent={issueCount === 0} />
        <Pair k="LLM-augmented" v={stats.withLlm} />
      </dl>
    </div>
  );
}

function Pair({ k, v, accent }: { k: string; v: number | string; accent?: boolean }) {
  return (
    <>
      <dt className="font-mono uppercase tracking-[0.1em] text-[10px] text-muted self-baseline">{k}</dt>
      <dd className={`tabular-nums text-right font-display font-bold text-t-20 tracking-[-0.02em] ${accent ? "text-good" : ""}`}>{v}</dd>
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted font-medium">
      {children}
    </div>
  );
}

function RankList({
  title, skills, onOpen,
}: { title: string; skills: Skill[]; onOpen: (name: string) => void }) {
  return (
    <div className="p-6 border-r border-rule last:border-r-0">
      <SectionLabel>{title}</SectionLabel>
      {skills.length === 0 ? (
        <p className="font-mono text-[11px] text-muted uppercase mt-3">// no data</p>
      ) : (
        <ol className="mt-3 flex flex-col">
          {skills.map((s, idx) => (
            <li key={s.name}>
              <button
                type="button"
                onClick={() => onOpen(s.name)}
                className="w-full flex items-baseline gap-3 border-b border-rule py-2.5 last:border-b-0 text-left hover:bg-[var(--hover)] -mx-1 px-1 transition-colors duration-75"
              >
                <span className="font-mono text-[12px] tabular-nums text-muted w-7 shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="font-display font-bold text-t-20 tracking-[-0.02em] truncate flex-1">
                  {s.name}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted shrink-0 hidden md:inline">
                  {s.category}
                </span>
                <span className={`font-display font-extrabold text-t-26 tabular-nums tracking-[-0.025em] shrink-0 ${scoreClassText(s.combinedPercentage)}`}>
                  {Math.round(s.combinedPercentage)}%
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function AttentionQueue({ items, onOpen }: { items: Skill[]; onOpen: (name: string) => void }) {
  return (
    <div className="p-6 border-r border-rule last:border-r-0">
      <SectionLabel>// Attention queue · {items.length} need work</SectionLabel>
      {items.length === 0 ? (
        <p className="font-display font-extrabold text-t-40 text-good leading-none tracking-[-0.03em] mt-3">
          All clear.
        </p>
      ) : (
        <ol className="mt-3 flex flex-col">
          {items.map((s) => (
            <li key={s.name}>
              <button
                type="button"
                onClick={() => onOpen(s.name)}
                className="w-full flex items-baseline gap-3 border-b border-rule py-2.5 last:border-b-0 text-left hover:bg-[var(--hover)] -mx-1 px-1"
              >
                <span className="font-mono text-[12px] tabular-nums text-muted w-7 shrink-0">
                  P{s.fixPriority ?? "—"}
                </span>
                <span className="font-display font-bold text-t-20 tracking-[-0.02em] truncate flex-1">
                  {s.name}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted shrink-0">
                  {s.brokenRefs.length > 0 ? `${s.brokenRefs.length} broken` : s.status}
                </span>
                <span className={`font-mono text-[12px] tabular-nums shrink-0 ${scoreClassText(s.combinedPercentage)}`}>
                  {Math.round(s.combinedPercentage)}%
                </span>
                <span className="text-muted font-mono text-[12px]">→</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Movers({ items, onOpen }: { items: Skill[]; onOpen: (name: string) => void }) {
  return (
    <div className="p-6">
      <SectionLabel>// Biggest movers</SectionLabel>
      {items.length === 0 ? (
        <p className="font-mono text-[11px] text-muted uppercase mt-3">// no deltas yet</p>
      ) : (
        <ol className="mt-3 flex flex-col">
          {items.map((s) => {
            const d = s.delta ?? 0;
            const cls = deltaClass(d);
            const clsText = cls === "good" ? "text-good" : cls === "bad" ? "text-bad" : "text-muted";
            return (
              <li key={s.name}>
                <button
                  type="button"
                  onClick={() => onOpen(s.name)}
                  className="w-full flex items-baseline gap-3 border-b border-rule py-2.5 last:border-b-0 text-left hover:bg-[var(--hover)] -mx-1 px-1"
                >
                  <span className={`font-display font-extrabold text-t-26 tabular-nums tracking-[-0.02em] shrink-0 w-14 ${clsText}`}>
                    {deltaSign(d)}
                  </span>
                  <span className="font-display font-bold text-t-20 tracking-[-0.02em] truncate flex-1">
                    {s.name}
                  </span>
                  <span className="font-mono text-[12px] tabular-nums text-muted shrink-0">
                    {Math.round(s.combinedPercentage)}%
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function SnapshotActivity({
  snapshots, liveSnapshot,
}: { snapshots: PortfolioSnapshot[]; liveSnapshot?: PortfolioSnapshot | null }) {
  const cells = useMemo(() => {
    const merged = liveSnapshot
      ? [...snapshots.filter((s) => s.generated !== liveSnapshot.generated), liveSnapshot]
      : snapshots;
    return merged.slice(-24);
  }, [snapshots, liveSnapshot]);

  if (cells.length === 0) {
    return (
      <section className="p-6 border-b border-fg">
        <SectionLabel>// Recent activity</SectionLabel>
        <p className="font-mono text-[11px] text-muted uppercase mt-3">// no snapshots yet · run build:data</p>
      </section>
    );
  }

  const latest = cells[cells.length - 1];
  const first = cells[0];
  const min = Math.min(...cells.map((c) => c.avgCombined));
  const max = Math.max(...cells.map((c) => c.avgCombined));
  const range = Math.max(1, max - min);

  return (
    <section className="p-6 border-b border-fg">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <SectionLabel>// Recent activity · {cells.length} snapshots</SectionLabel>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
          {first.generated.split("T")[0]} → {latest.generated.split("T")[0]}
        </span>
      </div>
      <div className="flex items-end gap-[3px] h-20" role="img" aria-label="Recent portfolio snapshots">
        {cells.map((c, i) => {
          const intensity = (c.avgCombined - min) / range;
          const heightPct = 20 + intensity * 80;
          const cls =
            c.avgCombined >= 70 ? "bg-good" : c.avgCombined >= 50 ? "bg-warn" : "bg-bad";
          const last = i === cells.length - 1;
          return (
            <div
              key={c.generated}
              className={`flex-1 min-w-[6px] hover-shift ${last ? "bg-accent outline outline-1 outline-fg outline-offset-[2px]" : cls}`}
              style={{ height: `${heightPct}%` }}
              title={`${c.generated.split("T")[0]} · ${c.avgCombined}% avg`}
            />
          );
        })}
      </div>
    </section>
  );
}
